class HeaderWrapper extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('.shopify-section-header');
    this.exitTimer = null;
    this.onEnterHandler = this.onEnter.bind(this);
    this.onLeaveHandler = this.onLeave.bind(this);
    this.addEventListener('mouseenter', this.onEnterHandler);
    this.addEventListener('mouseleave', this.onLeaveHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('mouseenter', this.onEnterHandler);
    this.removeEventListener('mouseleave', this.onLeaveHandler);
  }

  onEnter() {
    this.preventClose = true;
    this.header.classList.add('is-active');
    clearTimeout(this.exitTimer);
  }

  onLeave() {
    this.exitTimer = setTimeout(() => this.header.classList.remove('is-active'), 400);
  }
}

customElements.define('header-wrapper', HeaderWrapper);

// Vehicle Selector Modal Component - Now handled by CGFinderModalManager
// Legacy custom element for backward compatibility
customElements.define('vehicle-selector',
  class VehicleSelector extends HTMLElement {
    constructor() {
      super();
      // Check if CGFinderModalManager is available and initialized
      this.initializeModalManager();
    }

    connectedCallback() {
      // Ensure modal manager is available when element is connected
      this.initializeModalManager();
    }

    initializeModalManager() {
      // Wait for CGFinderModalManager to be available
      if (window.cgFinderModalManager) {
        console.log('VehicleSelector: Using existing CGFinderModalManager');
        return;
      }

      // If not available, wait a bit and try again
      setTimeout(() => {
        if (!window.cgFinderModalManager) {
          console.log('VehicleSelector: Initializing CGFinderModalManager');
          // Initialize if the modal manager class is available
          if (typeof CGFinderModalManager !== 'undefined') {
            window.cgFinderModalManager = new CGFinderModalManager();
          } else {
            console.warn('VehicleSelector: CGFinderModalManager class not found');
          }
        }
      }, 100);
    }

    // Legacy methods for backward compatibility - delegate to modal manager
    updateHeaderLabels() {
      // Avoid circular calls - let the modal manager handle this directly
      console.log('VehicleSelector.updateHeaderLabels() called - delegated to CGFinderModalManager');
      if (window.cgFinderModalManager) {
        window.cgFinderModalManager.updateHeaderLabelsDirectly();
      }
    }

    getSavedVehicleInfo() {
      if (window.cgFinderModalManager) {
        return window.cgFinderModalManager.getSavedVehicleInfo();
      }
      return null;
    }
  }
);

customElements.define('sticky-header',
  class StickyHeader extends HeaderWrapper {
    constructor() {
      super();
      this.header = document.querySelector('.section-header');
      this.main_menu = document.querySelector('.header__main-menu');
      this.stickyType = this.getAttribute('data-sticky-type');
      this.headerBounds = {};
      this.currentScrollTop = 0; 
      this.preventReveal = false;
      this.isScrolling = null;
      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => this.preventReveal = true;
      this.onResize = debounce(this.setHeaderHeight.bind(this), 100);
    }

    connectedCallback() {
      this.setHeaderHeight();
      window.addEventListener('resize', this.onResize);
      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, false);
      this.createObserver();

      this.initHeaderMenuLazyImages();
    }

    // ============================================
    // Lazy-load menu icon images inside header menus
    // ============================================
    initHeaderMenuLazyImages() {
      // Ensure header exists before querying
      if (!this.header) return;

      // Get all header menu containers
      const headerMenus = this.header.querySelectorAll('.header__menu');
      if (!headerMenus.length) return;

      headerMenus.forEach((menuEl) => {
        // Common handler for both hover and focus events
        const loadImagesOnce = (event) => {
          // Only load images for the menu that triggered the event
          const currentMenu = event.currentTarget;
          this.loadMenuImages(currentMenu);
        };

        // Load images when user hovers the menu (desktop)
        menuEl.addEventListener('mouseenter', loadImagesOnce, { once: true });

        // Also load images when the menu receives keyboard focus (accessibility)
        menuEl.addEventListener('focusin', loadImagesOnce, { once: true });
      });
    }

    loadMenuImages(menuEl) {
      if (!menuEl) return;

      // Only select lazy images inside the currently hovered/focused menu
      const imgs = menuEl.querySelectorAll('img[data-src]');

      imgs.forEach((img) => {
        // Skip if this image has already been loaded
        if (img.dataset.loaded === '1') return;

        const src = img.dataset.src;

        // Set the real `src` from `data-src` to trigger network loading
        if (src) {
          img.src = src;
          img.dataset.loaded = '1'; // Mark as loaded to avoid re-processing
        }
      });
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
    }

    createObserver() {
      const observer = new IntersectionObserver(([entry]) => {
        this.headerBounds = entry.intersectionRect;
        if (this.headerBounds.top === 0 && this.headerBounds.bottom === 0) {
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const { top, bottom } = entry.boundingClientRect;
          this.headerBounds = { top: scrollTop + top, bottom: scrollTop + bottom };
        }
        observer.disconnect();
      });

      observer.observe(this.header);
    }

    setHeaderHeight() {
      requestAnimationFrame(() => {
        document.documentElement.style.setProperty('--header-height', `${this.main_menu.offsetHeight + 16}px`);
      });
    }

    onScroll() {
      if (!this.headerBounds.bottom) return;
      requestAnimationFrame(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrolledPast = scrollTop > (this.headerBounds.bottom - 40); 
        // console.log(scrollTop);
        // console.log(this.headerBounds.bottom - 40);
        // const scrolledPast = scrollTop > this.headerBounds.top;
        if (this.stickyType === 'on-scroll-up') {
          if (scrollTop > this.currentScrollTop && scrolledPast) {
            this.header.classList.add('scrolled-past-header');
            if (!this.preventHide) this.hide();
          } else if (scrollTop < this.currentScrollTop && scrolledPast) {
            this.header.classList.add('scrolled-past-header');
            if (!this.preventReveal) {
              this.reveal();
            } else {
              clearTimeout(this.isScrolling);
              this.isScrolling = setTimeout(() => (this.preventReveal = false), 100);
              this.hide();
            }
          } else if (scrollTop <= this.headerBounds.top) {
            this.header.classList.remove('scrolled-past-header');
            this.reset();
          }
        } else {
          if (scrolledPast) {
            document.querySelectorAll(".header__menu").forEach(function(el) {
              el.classList.remove("active");
            });
            document.querySelectorAll(".header-content-search").forEach(function(el) {
              el.classList.remove("active");
            });
            this.closeSearchModal();
            this.header.classList.add('scrolled-past-header');
            if (!this.sticky()) {
              this.hide();
              this.reveal()
              // setTimeout(() => this.reveal(), 100);
            }
          } else if (scrollTop <= 150) {
            this.header.classList.remove('scrolled-past-header');
            this.reset();
          }
        }

        this.currentScrollTop = scrollTop;
      });
    }

    hide() {
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      document.body.classList.add('shopify-body-section-header-hidden', 'shopify-body-section-header-sticky');
      document.querySelectorAll(".header__menu").forEach(function(el) {
        el.classList.remove("active");
      });
      document.querySelectorAll(".header-content-search").forEach(function(el) {
        el.classList.remove("active");
      });
    //   this.closeMenuItems();
      this.closeSearchModal();
      this.closeHeaderDrawer();
    }

    reveal() {
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
      document.body.classList.add('shopify-body-section-header-sticky');
      document.body.classList.remove('shopify-body-section-header-hidden');
    }

    reset() {
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }

    sticky() {
      return this.header.classList.contains('shopify-section-header-sticky') && !this.header.classList.contains('shopify-section-header-hidden') && document.body.classList.contains('shopify-body-section-header-sticky') && !document.body.classList.contains('shopify-body-section-header-hidden');
    }

    closeHeaderDrawer() {
      this.headerDrawer = this.headerDrawer || this.header.querySelectorAll('header-drawer');
      this.headerDrawer.forEach((drawer) => drawer.isOpen && drawer.close());
    }

    // closeMenuItems() {
    //   this.menuItems = this.menuItems || this.header.querySelectorAll('header-menu, header-submenu');
    //   this.menuItems.forEach((menuItem) => menuItem.closeDropdown());
    // }

    closeSearchModal() {
      this.searchModals = this.searchModals || this.header.querySelectorAll('predictive-search');
      this.searchModals.forEach((searchModal) => searchModal.isOpen && searchModal.close(false));
    }
  }
)

customElements.define('account-icon',
  class AccountIcon extends HTMLSpanElement {
    constructor() {
      super();
      this.icon = this.querySelector('.icon');
    }

    connectedCallback() {
      document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
    }

    handleStorefrontSignInCompleted(event) {
      if (event?.detail?.avatar) this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
  , { extends: 'span' })

customElements.define('header-menu',
  class HeaderMenu extends HTMLDivElement {
    constructor() {
      super();
      this.header = document.querySelector('.header-wrapper');
      this.setPseudoclass();
      // this.addEventListener('focusin', this.openDropdown.bind(this));
      // this.addEventListener('focusout', this.closeDropdown.bind(this));
    }

    openDropdown() {
      this.classList.add('active');
    }

    // closeDropdown() {
    //   this.classList.remove('active');
    // }

    setPseudoclass() {
      if (!this.header) return;
      if (this.header.firstElementChild.classList.contains('header--top-center') || this.header.firstElementChild.classList.contains('header--top-left')) {
        this.style.setProperty('--p-header-menu', `var(--padding-bottom)`);
      } else {
        this.style.setProperty('--p-header-menu', `${Math.floor((this.header.offsetHeight - this.offsetHeight) / 2)}px`);
      }
    }
  }
  , { extends: 'div' })

// customElements.define('header-submenu',
//   class HeaderSubMenu extends HTMLDivElement {
//     constructor() {
//       super();
//       this.addEventListener('focusin', this.openDropdown.bind(this));
//       this.addEventListener('focusout', this.closeDropdown.bind(this));
//     }

//     openDropdown() {
//       this.classList.add('active');
//     }

//     closeDropdown() {
//       this.classList.remove('active');
//     }
//   }
//   , { extends: 'div' })


document.addEventListener('DOMContentLoaded', function () {
  // ---- Helpers ----
  const normalizePath = (href) => {
    try {
      const url = new URL(href, window.location.origin);
      // Get pathname only (ignoring query params and hash)
      let p = url.pathname.toLowerCase();
      if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
      return p;
    } catch {
      return '';
    }
  };
  const currentPath = normalizePath(window.location.href);

  function parseUrl(href) {
      try {
          const u = new URL(href, window.location.origin);
          // normalize pathname: lowercase + no trailing slash (except root)
          let p = u.pathname.toLowerCase();
          if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
          u.pathname = p;

          // normalize search params: sort by key + value
          if (u.search) {
              const sorted = new URLSearchParams();
              const entries = Array.from(u.searchParams.entries())
                  .sort(([ak, av], [bk, bv]) =>
                      ak.localeCompare(bk) || String(av).localeCompare(String(bv))
                  );
              entries.forEach(([k, v]) => sorted.append(k, v));
              u.search = sorted.toString() ? `?${sorted.toString()}` : '';
          }
          // ignore hash for matching
          u.hash = '';
          return u;
      } catch {
          return null;
      }
  }

  /**
   * Compare current location to a link.
   * - If the link has query params, require SAME pathname AND SAME param set (order-insensitive).
   * - If the link has NO params, match same path OR a deeper child path.
   */
  function urlsMatch(linkHref, currentUrl) {
      const linkUrl = parseUrl(linkHref);
      if (!linkUrl || !currentUrl) return false;

      const samePath = currentUrl.pathname === linkUrl.pathname;
      const childPath = currentUrl.pathname.startsWith(linkUrl.pathname + '/');

      if (linkUrl.search) {
          // link expects params -> require exact param match on same path
          return samePath && currentUrl.search === linkUrl.search;
      }
      // no params on link -> match same path or any child path
      return samePath || childPath;
  }

  const currentUrl = parseUrl(window.location.href);

  console.log(currentUrl);

  // ---- URL-based active states ----
  // 1) Level-1 links: add 'active' to the link itself if it matches
  document.querySelectorAll('.list-menu .list-menu__item--lv1[href]:not([href="#"])')
    .forEach((lv1) => {
      const p1 = normalizePath(lv1.href);
      if (urlsMatch(lv1.href, currentUrl)) {
        // lv1.classList.add('active');
        lv1.classList.add('link-active');
      }
    });

  // 2) Level-2 links: if any matches, add 'active' to the parent .header__menu block
  document.querySelectorAll('.list-menu .list-menu__item--lv2[href]')
    .forEach((lv2) => {
      const p2 = normalizePath(lv2.href);
      // console.log(lv2);
      if (urlsMatch(lv2.href, currentUrl)) {
        const block = lv2.closest('.header__menu');
        if (block) {
          // block.classList.add('active');
          block.classList.add('link-active');
        }
      }
    });

  // 3) Footer links: if any matches, add 'active' to the parent .header__menu block
  document.querySelectorAll('.list-menu .footer-submemu a[href]')
  .forEach((lv3) => {
    const p3 = normalizePath(lv3.href);
    if (urlsMatch(lv3.href, currentUrl)) {
      const block = lv3.closest('.header__menu');
      if (block) {
        // block.classList.add('active');
        block.classList.add('link-active');
      }
    }
  });
});




